import Vue from 'vue';
import {CompaniesResponse, Company} from '@/interfaces/company.interface';
import CompanyService from '@/services/api/company.service';
import {RootState} from '@/store/store';
import {Module} from 'vuex';
import {VuetifyPaginationInterface} from "@/interfaces/vuetify-pagination.interface";

export const mutations = {
    SET_COMPANIES(state: any, response: CompaniesResponse) {
        Vue.set(state, 'companies', response.companies);
        Vue.set(state, 'page', response.page);
        Vue.set(state, 'itemsPerPage', response.itemsPerPage);
        Vue.set(state, 'totalItems', response.totalItems);
        Vue.set(state, 'pagination', {
            ...state.pagination,
            ...{totalItems: response.totalItems}
        });
    },
    SET_COMPANY(state: any, company: Company) {
        Vue.set(state, 'company', company);
    },
    CREATE_COMPANY(state: any, {company}: { company: Company }) {
        Vue.set(state, 'companies', [...state.companies, company]);
    },
    UPDATE_COMPANY(state: any, {company}: { company: Company }) {
        Vue.set(
            state,
            'companies',
            state.companies.map((u: Company) => u.uuid === company.uuid ? {...u, ...company} : u)
        );
    },
    DELETE_COMPANY(state: any, {company}: { company: Company }) {
        Vue.set(state, 'companies', state.companies.filter((u: Company) => u.uuid !== company.uuid));
    },
    setCompaniesFilter(state: any, filters: any) {
        Vue.set(state, 'companyFilters', {...state.companyFilters, ...filters});
    },
    setPagination(state: any, {pagination}: { pagination: VuetifyPaginationInterface }) {
        Vue.set(state, 'pagination', pagination);
    },
};

export const actions = {
    async fetchCompanies({commit, state}: any) {
        commit('LOADING_START', null, {root: true});
        try {
            const filters = state.companyFilters;
            const pagination = state.pagination;
            const response: CompaniesResponse = await CompanyService.getAll(pagination, filters);
            commit('LOADING_FINISH', null, {root: true});
            commit('SET_COMPANIES', {
                companies: response.companies,
                page: response.page,
                itemsPerPage: response.itemsPerPage,
                totalItems: response.totalItems
            });
        } catch (e) {
            commit('LOADING_FINISH', null, {root: true});
            console.log('error', e);
        }
    },
    SET_COMPANY({commit}: any, company: Company) {
        commit('SET_COMPANY', company);
    },
    UNSET_COMPANY({commit}: any) {
        commit('SET_COMPANY', {});
    },
    async createCompany(
        {commit, dispatch}: any,
        {company, successAction, successPayload}: { company: Company, successAction: string, successPayload: any }) {
        commit('LOADING_START', null, {root: true});
        try {
            const response = await CompanyService.create(company);
            commit('CREATE_COMPANY', {company: response});
            if (successAction) dispatch(successAction, successPayload);
        } catch (e) {
            console.log(e);
            dispatch('SET_ALERT_MESSAGE', 'Crating company failed', {root: true});
        } finally {
            commit('LOADING_FINISH', null, {root: true});
        }
    },
    async UPDATE_COMPANY(
        {commit, dispatch}: any,
        {company, successAction, successPayload}: { company: Company, successAction: string, successPayload: any }) {
        commit('LOADING_START', null, {root: true});
        try {
            const response = await CompanyService.update(company);
            commit('UPDATE_COMPANY', {company: response});
            if (successAction) dispatch(successAction, successPayload);
            dispatch('SET_ALERT_MESSAGE', 'Company updated', {root: true});
        } catch (e) {
            dispatch('SET_ALERT_MESSAGE', 'Updating company failed', {root: true});
            console.log(e);
        } finally {
            commit('LOADING_FINISH', null, {root: true});
        }
    },
    async deleteWithConfirmation({commit}: any, {company}: { company: Company }) {
        commit('SHOW_CONFIRMATION', {
            message: `Delete ${company.name}?`,
            action: 'companies/delete',
            payload: {company}
        }, {root: true});
    },
    async delete({commit, dispatch}: any, {company}: { company: Company }) {
        commit('LOADING_START', null, {root: true});
        try {
            if (!company.uuid) return dispatch('SET_ALERT_MESSAGE', `Can't delete a company without a uuid`, {root: true});
            await CompanyService.delete(company.uuid);
            await commit('DELETE_COMPANY', {company});
        } catch (e) {
            console.log(e);
            dispatch('SET_ALERT_MESSAGE', 'Deleting company failed', {root: true});
        } finally {
            commit('LOADING_FINISH', null, {root: true});
        }
    },
    setCompaniesFilter({commit}: any, filters: any) {
        commit('setCompaniesFilter', filters);
    },
    setPagination({commit}: any, {pagination}: {pagination: VuetifyPaginationInterface}) {
        commit('setPagination', {pagination});
    }
};

export interface CompaniesState {
    companies: Company[],
    companyFilters: {
        search: string
    },
    addCompanyDialogVisible: boolean,
    editCompanyDialogVisible: boolean,
    pagination: VuetifyPaginationInterface,
    totalItems: number,
    page: number,
    itemsPerPage: number,
}

export const companiesState: CompaniesState = {
    companies: [],
    totalItems: 0,
    page: 1,
    itemsPerPage: 50,
    companyFilters: {
        search: ''
    },
    addCompanyDialogVisible: false,
    editCompanyDialogVisible: false,
    pagination: {
        page: 1,
        itemsPerPage: 50
    }
};

export const companiesStoreModule: Module<CompaniesState, RootState> = {
    namespaced: true,
    state: companiesState,
    actions,
    mutations,
};
